/**
 * ConnectionDoneCard — the ONE post-connect confirmation (2026-08-18 UX
 * overhaul, "done → wow" beat). Renders after a connect completes: the app
 * is connected, the defaults are stated as glanceable ONE-TAP chips (never a
 * blocking question — the founder default is Everything, and storage apps
 * default to all folders exactly like the old sheet's dismiss path), and a
 * suggested first action launches chat with a seeded prompt — connect → try
 * → result, usually under a minute.
 */

import React from 'react';
import { ACCESS_TIER_LABELS, DEFAULT_ACCESS_TIER } from '../ComposioClient';
import { firstActionForApp } from '../appSearch';
import panelTheme from '../panelTheme';

export interface ConnectionDoneCardProps {
  slug: string;
  displayName: string;
  isStorage: boolean;
  /** Second-account connect: the card celebrates the ADDED account. */
  addedAccount?: boolean;
  /** Already-connected courtesy notice (the id-diff found no new account). */
  alreadyConnected?: boolean;
  /**
   * Browser-signin (custom) connection (2026-08-19): same wow moment, but a
   * browser session has no per-tool access verbs to edit — the chips row is
   * replaced by an honest one-liner.
   */
  customConnection?: boolean;
  onOpenAccess: () => void;
  onOpenFolders: () => void;
  onDismiss: () => void;
  /** Optional override for the seeded try-it prompt. */
  firstActionPrompt?: string;
  /** Email journey (2026-08-20): the post-connect probe's verdict — upgrades
   *  the card to confirmed / mismatch / no-mailbox. Undefined while the
   *  probe runs (the connected outcome never waits on it). */
  verification?: {
    verdict: 'confirmed' | 'mismatch' | 'no-mailbox' | 'unverified';
    identity: string | null;
    possibleAlias: boolean;
  } | null;
  /** The inbox the user said they were connecting. */
  intendedAddress?: string | null;
  /** Mismatch remedy: remove the wrong sign-in and relaunch with the same intent. */
  onRetryAs?: (intendedAddress: string) => void;
}

const chip: React.CSSProperties = {
  fontSize: '0.76rem', fontWeight: 600, padding: '0.25rem 0.65rem',
  borderRadius: panelTheme.radiusChip, border: '1px solid transparent',
  background: panelTheme.goldTintStrong, color: panelTheme.goldInk,
  cursor: 'pointer',
};

export const ConnectionDoneCard: React.FC<ConnectionDoneCardProps> = ({
  slug,
  displayName,
  isStorage,
  addedAccount,
  alreadyConnected,
  customConnection,
  onOpenAccess,
  onOpenFolders,
  onDismiss,
  firstActionPrompt,
  verification,
  intendedAddress,
  onRetryAs,
}) => {
  const prompt = firstActionPrompt || firstActionForApp(slug, displayName);
  const accessLabel = (ACCESS_TIER_LABELS[DEFAULT_ACCESS_TIER] || 'Full access').toLowerCase();

  const tryItNow = () => {
    // NEW THREAD + IMMEDIATE SEND (2026-08-18 owner feedback): the try-it
    // suggestion opens a fresh thread and sends the prompt right away —
    // never a composer draft. preventDefault from the chat system is the
    // handled handshake; without one mounted, fall back to the old
    // seeded-draft channel so the prompt is never lost.
    const sendNow = new CustomEvent('prest0n:chat-new-thread-send', {
      detail: { text: prompt },
      cancelable: true,
    });
    document.dispatchEvent(sendNow);
    if (!sendNow.defaultPrevented) {
      document.dispatchEvent(
        new CustomEvent('globalchat:open', { detail: { triggerPhrase: prompt } })
      );
    }
    onDismiss();
  };

  return (
    <div
      data-testid={`connection-done-${slug}`}
      style={{
        display: 'flex', flexDirection: 'column', gap: '0.55rem',
        padding: '0.9rem 1.1rem', marginBottom: '1rem',
        background: panelTheme.successBg,
        border: `1px solid ${panelTheme.successBorder}`,
        borderRadius: panelTheme.radiusCard,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.95rem', fontWeight: 700, color: panelTheme.successDeep }}>
          ✓ {displayName}{' '}
          {alreadyConnected
            ? '— that account was already connected. you’re all set.'
            : addedAccount
              ? '— another account added'
              : 'connected'}
        </span>
        <button
          onClick={onDismiss}
          aria-label="dismiss"
          style={{
            marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
            color: panelTheme.successDeep, fontSize: '1.05rem', lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
      {/* Email-journey verification (2026-08-20): who ACTUALLY signed in.
          Undefined while the probe runs — the card never waits on it. */}
      {verification === undefined && intendedAddress && (
        <div data-testid="verify-checking" style={{ fontSize: '0.78rem', color: panelTheme.grayText }}>
          checking which account signed in…
        </div>
      )}
      {verification && verification.verdict === 'confirmed' && verification.identity && (
        <div data-testid="verify-confirmed" style={{ fontSize: '0.8rem', color: panelTheme.successDeep }}>
          connected as <strong>{verification.identity}</strong>
          {intendedAddress ? ' — the inbox you meant' : ''}
        </div>
      )}
      {verification && verification.verdict === 'mismatch' && (
        <div
          data-testid="verify-mismatch"
          style={{
            fontSize: '0.8rem', color: panelTheme.goldInk, background: panelTheme.goldTint,
            borderRadius: panelTheme.radiusChip, padding: '0.55rem 0.75rem',
            display: 'flex', flexDirection: 'column', gap: '0.45rem', lineHeight: 1.5,
          }}
        >
          <span>
            this connection signed in as <strong>{verification.identity}</strong>
            {intendedAddress ? <> — you meant <strong>{intendedAddress}</strong></> : null}.
            {verification.possibleAlias
              ? ' the domains match, so it may be an alias of the same inbox — keep it if that’s right.'
              : ''}
          </span>
          <span style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {intendedAddress && onRetryAs && (
              <button
                data-testid="verify-retry-as"
                onClick={() => onRetryAs(intendedAddress)}
                style={{ ...chip }}
              >
                connect {intendedAddress} instead
              </button>
            )}
            <button
              data-testid="verify-keep"
              onClick={onDismiss}
              style={{ ...chip, background: 'transparent', border: `1px solid ${panelTheme.grayBorder}`, color: panelTheme.grayText }}
            >
              keep this sign-in
            </button>
          </span>
        </div>
      )}
      {verification && verification.verdict === 'no-mailbox' && (
        <div
          data-testid="verify-no-mailbox"
          style={{
            fontSize: '0.8rem', color: panelTheme.danger,
            border: `1px solid ${panelTheme.danger}`, borderRadius: panelTheme.radiusChip,
            padding: '0.55rem 0.75rem', display: 'flex', flexDirection: 'column',
            gap: '0.45rem', lineHeight: 1.5,
          }}
        >
          <span>
            ⚠ this sign-in{verification.identity ? <> (<strong>{verification.identity}</strong>)</> : null} has
            no usable mailbox — usually an admin or unlicensed account signed in. reconnect
            signed in as the inbox itself{intendedAddress ? <> (<strong>{intendedAddress}</strong>)</> : null},
            or ask your admin to give that account a mailbox license. an admin approves the
            app from their admin console — they never sign in with their own account.
          </span>
          {intendedAddress && onRetryAs && (
            <span>
              <button
                data-testid="verify-retry-no-mailbox"
                onClick={() => onRetryAs(intendedAddress)}
                style={{ ...chip }}
              >
                try again as {intendedAddress}
              </button>
            </span>
          )}
        </div>
      )}
      {!alreadyConnected && customConnection && (
        <div style={{ fontSize: '0.78rem', color: panelTheme.grayText }}>
          {/* Honest lane copy (PLAN §7.4): a browser session has no per-tool
              verbs to gate — prest0n uses this exactly as you. */}
          Prest0n uses this sign-in exactly as you — manage or remove it
          anytime from the connected tiles above.
        </div>
      )}
      {!alreadyConnected && !customConnection && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
          {/* Defaults as glanceable one-tap chips — informational, never a
              gate. Copy reads the ONE default constant. */}
          <button onClick={onOpenAccess} data-testid="done-access-chip" title="Change what Prest0n may do here" style={chip}>
            🔑 {accessLabel} (default)
          </button>
          {isStorage && (
            <button onClick={onOpenFolders} data-testid="done-folders-chip" title="Choose which folders Prest0n can use" style={chip}>
              📁 folders: all (default)
            </button>
          )}
          <span style={{ fontSize: '0.74rem', color: panelTheme.grayText }}>
            tap a chip to change — or leave it, you can adjust anytime.
          </span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <button
          onClick={tryItNow}
          data-testid="done-first-action"
          style={{
            padding: '0.45rem 1rem', borderRadius: panelTheme.radiusPill, border: 'none',
            background: panelTheme.ink, color: '#fff', fontWeight: 700,
            fontSize: '0.82rem', cursor: 'pointer', textTransform: 'lowercase',
          }}
        >
          try: {prompt}
        </button>
        <button
          onClick={onDismiss}
          data-testid="done-dismiss"
          style={{
            background: 'none', border: 'none', color: panelTheme.grayText,
            fontSize: '0.8rem', cursor: 'pointer', textTransform: 'lowercase',
            textDecoration: 'underline',
          }}
        >
          done
        </button>
      </div>
    </div>
  );
};

export default ConnectionDoneCard;
